/**
 * Dragging a mailbox heading in the rail to put the mailboxes in another order.
 *
 * The browser's own drag and drop: the heading is what you pick up, the whole tree is
 * where you drop, and the upper or lower half of it says before or after. A line on the
 * tree's edge shows where it will land. The keyboard does the same with Alt+Up and
 * Alt+Down on the heading, one place at a time.
 */
import { useState, type DragEvent, type KeyboardEvent } from "react";

export interface MailboxDrag {
  /** This tree is the one being carried. */
  carried: boolean;
  /** Where the carried tree would land, against this one. */
  line: "before" | "after" | null;
  /** On the heading: what picks the tree up. */
  handle: {
    draggable: true;
    onDragStart: (event: DragEvent) => void;
    onDragEnd: () => void;
  };
  /** On the whole tree: where it can be dropped. */
  target: {
    onDragOver: (event: DragEvent<HTMLElement>) => void;
    onDrop: (event: DragEvent) => void;
  };
  /** On the heading button: Alt+Up and Alt+Down. */
  onKeyDown: (event: KeyboardEvent) => void;
}

export function useMailboxDrag(ids: string[], onMove: (id: string, target: string, after: boolean) => void) {
  const [carried, setCarried] = useState<string | null>(null);
  const [over, setOver] = useState<{ id: string; after: boolean } | null>(null);

  const end = () => {
    setCarried(null);
    setOver(null);
  };

  return (id: string): MailboxDrag => ({
    carried: carried === id,
    line: carried && carried !== id && over?.id === id ? (over.after ? "after" : "before") : null,
    handle: {
      draggable: true,
      onDragStart: (event) => {
        event.dataTransfer.effectAllowed = "move";
        // Some browsers start no drag without data.
        event.dataTransfer.setData("text/plain", id);
        setCarried(id);
      },
      onDragEnd: end,
    },
    target: {
      onDragOver: (event) => {
        if (!carried) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
        const box = event.currentTarget.getBoundingClientRect();
        const after = event.clientY > box.top + box.height / 2;
        if (over?.id !== id || over.after !== after) setOver({ id, after });
      },
      onDrop: (event) => {
        event.preventDefault();
        if (carried && carried !== id && over?.id === id) onMove(carried, id, over.after);
        end();
      },
    },
    onKeyDown: (event) => {
      if (!event.altKey || (event.key !== "ArrowUp" && event.key !== "ArrowDown")) return;
      event.preventDefault();
      const at = ids.indexOf(id);
      const up = event.key === "ArrowUp";
      const next = ids[up ? at - 1 : at + 1];
      if (next) onMove(id, next, !up);
    },
  });
}
